/**
 * AppLayout Component
 * 
 * This component serves as the main layout wrapper for the Trip Planner application.
 * It provides a consistent structure across different pages including:
 * - Header
 * - Navigation breadcrumbs
 * - Search functionality (optional)
 * - Page title and subtitle
 * - Trip dates (when applicable)
 * - Bottom navigation
 * 
 * The layout adapts based on the current route and selected trip.
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useTrip, formatDate } from '@trip-planner-monorepo/shared';
import Header from './Header';
import NavigationHistory from './NavigationHistory';
import BottomNav from './BottomNav';
import { helpAssistance, helpService } from '@trip-planner-monorepo/app-features';
import './AppLayout.css';

/**
 * AppLayout Component
 * 
 * @param {React.ReactNode} children - The page content to render within the layout
 */
const AppLayout = ({ children, fullHeight = false }) => {
  const { currentTrip, selectedTripId, updateTripLocation } = useTrip();
  const location = useLocation();
  const { currentUser } = useAuth();
  const mainContentRef = useRef(null);
  // Defensive state initialization: provide a safe fallback if service state is unavailable
  const initialHelpState = helpService && typeof helpService.getState === 'function'
    ? helpService.getState()
    : { isOpen: false };

  const [helpState, setHelpState] = React.useState(initialHelpState);

  useEffect(() => {
    if (helpService && typeof helpService.subscribe === 'function') {
      const unsubscribe = helpService.subscribe((newState) => {
        if (newState) setHelpState(newState);
      });
      return unsubscribe;
    }
  }, []);

  /**
   * Effect: Update Trip Location Tracking
   * 
   * PURPOSE:
   * Tracks the user's last visited location within each trip for a better
   * user experience when returning to the application.
   * 
   * PROCESS:
   * Whenever the route changes, update the stored location for the current trip.
   * This allows the app to restore users to their last position in a trip.
   */
  useEffect(() => {
    if (currentUser && selectedTripId) {
      updateTripLocation(selectedTripId, location.pathname);
    }
  }, [currentUser, selectedTripId, location.pathname, updateTripLocation]);

  /**
   * Effect: Scroll main content to top on route change
   *
   * The window has overflow:hidden — all page scrolling happens inside the
   * <main className="main-content"> div.  window.scrollTo() has no effect here,
   * so we hold a ref to the element and reset its scrollTop whenever the path changes.
   */
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  /**
   * Page Information Generator
   * 
   * PURPOSE:
   * Dynamically generates the appropriate page title and subtitle based on the current route.
   * 
   * PROCESS:
   * 1. Examines the current URL path
   * 2. Determines which section of the app is active (days, places, travel, etc.)
   * 3. Creates a title that incorporates both the section name and trip name when applicable
   * 4. Provides contextual subtitles for each section
   * 
   * OPTIMIZATION:
   * Uses React.useMemo to prevent unnecessary recalculations unless the path or trip changes
   */
  const pageInfo = React.useMemo(() => {
    if (location.pathname === '/') {
      return {
        title: 'My Trips',
        subtitle: 'Plan and organize your journeys'
      };
    }

    const path = location.pathname;

    // Define page configurations
    const pages = {
      days: {
        title: currentTrip?.name || 'Trip Details',
        subtitle: 'Plan your day-by-day activities'
      },
      places: {
        title: `Places - ${currentTrip?.name || 'Trip Details'}`,
        subtitle: 'Manage locations and points of interest'
      },
      travel: {
        title: `Travel - ${currentTrip?.name || 'Trip Details'}`,
        subtitle: 'Transportation and movement details'
      },
      accommodation: {
        title: `Accommodation - ${currentTrip?.name || 'Trip Details'}`,
        subtitle: 'Where you\'ll be staying'
      },
      extras: {
        title: `Extras - ${currentTrip?.name || 'Trip Details'}`,
        subtitle: 'Additional trip details and notes'
      }
    };

    // Determine current section
    let section = 'days';
    if (path.includes('/places')) section = 'places';
    else if (path.includes('/travel')) section = 'travel';
    else if (path.includes('/accommodation')) section = 'accommodation';
    else if (path.includes('/extras')) section = 'extras';

    return pages[section];
  }, [location.pathname, currentTrip]);

  /**
   * Trip Date Formatter
   * 
   * PURPOSE:
   * Formats trip start and end dates into user-friendly strings and calculates trip duration.
   * 
   * PROCESS:
   * 1. Checks if trip has valid start and end dates
   * 2. Formats the dates using the application's date formatting utility
   * 3. Calculates the trip duration in days
   * 4. Returns formatted date range and duration with appropriate pluralization
   * 
   * RETURNS:
   * - null if dates are not available
   * - Object with display (formatted date range) and duration properties
   */
  const getTripDates = () => {
    if (!currentTrip?.startDate || !currentTrip?.endDate) return null;

    const start = formatDate(currentTrip.startDate);
    const end = formatDate(currentTrip.endDate);

    // Calculate trip duration in days (inclusive of start and end dates)
    const days = Math.ceil(
      (new Date(currentTrip.endDate) - new Date(currentTrip.startDate)) / (1000 * 60 * 60 * 24)
    ) + 1;

    return {
      display: `${start} - ${end}`,
      duration: `${days} day${days !== 1 ? 's' : ''}`  // Proper pluralization
    };
  };

  /**
   * Component Rendering
   * 
   * STRUCTURE:
   * - Container div with app-layout class
   * - Header component for app-wide navigation
   * - Main content area with:
   *   - Page header (title, subtitle, trip dates)
   *   - Breadcrumb navigation
   *   - Page content (children)
   * - Bottom navigation component
   * 
   * CONDITIONAL RENDERING:
   * - Trip dates only appear when a trip is selected and has valid dates
   */
  return (
    <div
      className={`app-layout ${helpState.isOpen ? 'assistance-open' : ''}`}
      style={helpState.isOpen ? { gridTemplateColumns: `1fr ${helpState.dockWidth}px` } : {}}
    >
      <Header />

      <main className={`main-content${fullHeight ? ' main-content--full-height' : ''}`} ref={mainContentRef}>
        <div className="page-header">
          <div className="page-title">
            <div className="title-content">
              <h1>{pageInfo.title}</h1>
              <p className="page-subtitle">{pageInfo.subtitle}</p>
            </div>
            {/* Show trip info section when a trip is selected */}
            {currentTrip && (
              <div className="trip-dates">
                {getTripDates() ? (
                  <>
                    <span className="dates">{getTripDates().display}</span>
                    <span className="duration">{getTripDates().duration}</span>
                  </>
                ) : (
                  <span className="dates">No dates set</span>
                )}
                {/* Documents button - always show when a trip is selected */}
                <button
                  className="documents-button"
                  onClick={() => window.location.href = `/trips/${currentTrip.id}/documents`}
                  title="View Documents"
                >
                  📄 Documents
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Breadcrumb navigation for hierarchical context */}
        <NavigationHistory />

        {/* Main page content (passed as children) */}
        <div className={`page-content${fullHeight ? ' page-content--full-height' : ''}`}>
          {children}
        </div>
      </main>

      {/* Bottom navigation for mobile-friendly access to key sections */}
      <BottomNav />

      {/* Integration of the Assistance Dock */}
      {helpAssistance && helpAssistance.AssistanceDock && <helpAssistance.AssistanceDock />}
    </div>
  );
};

export default AppLayout;